import type { AdminUserDetail } from 'src/types/admin/user-detail';
import type {
  AddCreditsPayload,
  UpdateUserGroupsPayload,
  UpdateCreditSettingsPayload,
  UpdateUserAdminRolesPayload,
  UpdateUserGeneralDataPayload,
} from './schemas';

import useSWR from 'swr';
import { useMemo } from 'react';

import axiosInstance, { fetcher, endpoints } from 'src/lib/axios';

import { userCacheKeys } from './user-management';
import { usePaginatedMutation } from '../../../lib/swr';

type UseGetUserDetailParams = {
  mainGroupId?: number;
  userId?: number;
};

export function useGetUserDetail({ mainGroupId, userId }: UseGetUserDetailParams) {
  const url =
    mainGroupId && userId ? endpoints.admin.management.userDetail(mainGroupId, userId) : null;

  const { data, isLoading, error, isValidating, mutate } = useSWR<AdminUserDetail>(url, fetcher);

  return useMemo(
    () => ({
      user: data,
      userLoading: isLoading,
      userError: error,
      userValidating: isValidating,
      userRefresh: mutate,
    }),
    [data, error, isLoading, isValidating, mutate]
  );
}

export async function updateUserGeneralData(
  mainGroupId: number,
  userId: number,
  data: UpdateUserGeneralDataPayload
): Promise<void> {
  await axiosInstance.patch(
    endpoints.admin.management.updateUserGeneralData(mainGroupId, userId),
    data
  );
}

export function useUpdateUserGroups(mainGroupId: number, userId: number) {
  return usePaginatedMutation<UpdateUserGroupsPayload>(
    userCacheKeys.users(mainGroupId),
    async (data: UpdateUserGroupsPayload): Promise<void> => {
      await axiosInstance.patch(
        endpoints.admin.management.updateUserGroups(mainGroupId, userId),
        data
      );
    }
  );
}

export function useUpdateUserAdminRoles(mainGroupId: number, userId: number) {
  return usePaginatedMutation<UpdateUserAdminRolesPayload>(
    userCacheKeys.users(mainGroupId),
    async (data: UpdateUserAdminRolesPayload): Promise<void> => {
      await axiosInstance.patch(
        endpoints.admin.management.updateUserAdminRoles(mainGroupId, userId),
        data
      );
    }
  );
}

export async function bookCredits(
  mainGroupId: number,
  userId: number,
  data: AddCreditsPayload
): Promise<void> {
  await axiosInstance.post(endpoints.admin.management.bookCredits(mainGroupId, userId), data);
}

export async function updateCreditSettings(
  mainGroupId: number,
  userId: number,
  data: UpdateCreditSettingsPayload
): Promise<void> {
  await axiosInstance.patch(
    endpoints.admin.management.updateCreditSettings(mainGroupId, userId),
    data
  );
}
